import { useTempDataContext } from '@fox/atoms/src/temp-data-context/index';
import { sendUpdate } from '@fox/extensions/src/extension-context';
import { useI18n } from '@fox/extensions/src/use-i18n';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { filterOutConditionals, getAllBlocksFromLayout } from '../blocks/form-block/conditionals/helpers';
import { ensureAnswers } from '../blocks/form-block/util/ensure-answers';
import { useFormErrorContext } from '../form-error-context/form-error-context';
import { EventCapacityStatus } from '../form-event-context/contracts';
import { useFormEventContext } from '../form-event-context/form-event-context';
import { IQuestionAnswer } from '../form-root/contracts';
import { validateFormClientSide } from '../form-root/helpers';
import {
	DEFAULT_QUESTION_STYLES,
	FORM_QUESTION_ANSWERED_TOPIC,
	FORM_SET_ANSWERS,
	FormRenderMode,
	IFormRuntimeContext,
	IFormRuntimeContextProps,
	QuestionStyle,
	QuestionStyles,
	QuestionStyleTypes,
	SurveyState
} from './contracts';
import { FormRuntimeContext } from './form-runtime-context';
import { buildTransitionMap, goToNextFormPage } from './helpers';
import { LoadingOverlay } from './loading-overlay';
import { prefillAnswers } from './prefill';

export const FormRuntimeContextProvider = ({
	children,
	submitButtonDisabled,
	onSubmit,
	onSubmitted,
	uploadFile,
	ownerId,
	ownerType,
	targetInfo,
	styles,
	inLocalSurveyMode,
	getPointsTotal,
	complete,
	surveyState,
	formRenderMode,
	initialAnswers,
	layout,
	submitButtonLabel,
	formRequestId,
	formId
}: { children: React.ReactNode } & IFormRuntimeContextProps) => {
	const prefilledAnswers = useMemo(() => {
		//no prefill in design mode
		if (formRenderMode !== FormRenderMode.runtime) return [];
		return prefillAnswers(layout);
	}, [layout]);
	const answers = useRef<IQuestionAnswer[]>(initialAnswers ?? prefilledAnswers ?? []);
	const [currentPage, setCurrentPage] = useState<number>(0);
	const [prevPage, setPrevPage] = useState<number[]>([]);
	const [isValidating, setIsValidating] = useState(false);
	const i18n = useI18n();

	const formErrorContext = useFormErrorContext();
	const { setErrors, clearSubmissionState, errors } = formErrorContext ?? {};
	const tempData = useTempDataContext();
	const { createTicketReservation, extendReservation, findReservationById, getTicketDetails, event } = useFormEventContext();

	const getAnswers = (): IQuestionAnswer[] => {
		if (formRenderMode === FormRenderMode.design) return []; //could be weird that preview mode answers persist?
		return answers.current;
	};

	const getAnswer = (questionName: string): IQuestionAnswer | undefined => {
		return answers.current.find(c => c.questionName === questionName);
	};

	const setAnswers = (a: IQuestionAnswer[]) => {
		answers.current = a;
		sendUpdate(FORM_SET_ANSWERS);
	};

	const answerQuestion = (a: IQuestionAnswer) => {
		const updatedAnswers = [...answers.current];
		const idx = updatedAnswers.findIndex(ans => ans.questionName === a.questionName);
		//find the type of this answers, certain other types of answer need a rerender
		//find whether any blocks are conditionally rendered based on this answer
		if (idx !== -1) {
			updatedAnswers[idx] = a;
		} else {
			updatedAnswers.push(a);
		}
		answers.current = updatedAnswers;
		sendUpdate(`form-block-${a.questionName}`);
		//this is to re-render next/submit/rsvp buttons to make sure they are enabled/disabled correctly
		sendUpdate(FORM_QUESTION_ANSWERED_TOPIC);
		if (setErrors) {
			const prevErrorMsg = errors?.[a.questionName]?.message;
			const hasPrevError = prevErrorMsg !== undefined;
			const hasNewError = a.error !== undefined;

			const errorChanged = (hasNewError && (!hasPrevError || prevErrorMsg !== a.error)) || (!hasNewError && hasPrevError);

			if (errorChanged) {
				if (hasNewError) {
					setErrors(prev => ({ ...prev, [a.questionName]: { message: a.error } }));
				} else {
					setErrors(prev => {
						const { [a.questionName]: _, ...rest } = prev;
						return rest;
					});
				}
			}
		}
	};

	const goToNextPage = useCallback((nextSection: number) => goToNextFormPage(nextSection, currentPage, setCurrentPage, setPrevPage), [currentPage]);

	const sections = layout.layouts?.[0]?.props?.sections ?? [];

	const transitionMap = useMemo(() => buildTransitionMap(sections), [sections]);

	const onWaitList = event?.status === EventCapacityStatus.OnWaitlist;

	const nextPage = async () => {
		clearSubmissionState?.();

		const branchedAnswers = sections[currentPage ?? 0]?.fields
			?.filter(f => f.type.includes('Choice') === true)
			.map(choiceField => {
				const choiceAnswers = (answers.current ?? []).find(a => a.questionName === choiceField.name);
				try {
					const choiceBranches = JSON.parse(choiceField.metadataJson ?? '{}')
						?.choices?.map(c => {
							const chosen = choiceAnswers?.answer?.answerJson
								? JSON.parse(choiceAnswers.answer.answerJson).find(l => l === c.value) !== undefined
								: choiceAnswers?.answer?.answerString
									? choiceAnswers?.answer?.answerString === c.value
									: false;
							return {
								value: c.value,
								branchId: c.branchId,
								chosen: chosen
							};
						})
						.filter(c => c.chosen)
						.filter(c => c.branchId !== undefined);
					if (choiceBranches?.length > 0) {
						return choiceBranches[choiceBranches.length - 1].branchId;
					}
				} catch {}
				return null;
			})
			.filter(f => f !== null);

		const nextSection =
			branchedAnswers.length > 0
				? sections.findIndex(s => s.branchId === branchedAnswers[branchedAnswers.length - 1])
				: transitionMap[currentPage ?? 0].next;

		const currentFields = sections[currentPage ?? 0].fields;
		const updatedAnswers = [...(answers.current ?? [])];
		const valid =
			formRenderMode === FormRenderMode.runtime
				? await validateFormClientSide(
						formRequestId,
						ownerId,
						currentFields,
						sections,
						updatedAnswers,
						tempData,
						nextSection => goToNextPage(nextSection),
						{
							createTicketReservation,
							extendReservation,
							findReservationById,
							getTicketDetails
						},
						setErrors,
						false,
						layout,
						onWaitList
					)
				: true;

		if (valid) {
			goToNextPage(nextSection);
		} else {
			console.log('Invalid validations, cannot progress');
		}
		setAnswers(updatedAnswers);
	};

	// This effect is to skip the first page ONLY if it is a fully prefilled page.
	useEffect(() => {
		if (!prefilledAnswers?.length) return;
		// If there is only a single section, don't bother
		if (!(layout.layouts?.[0]?.props?.sections.length > 1)) return;

		const fields = layout.layouts?.[0]?.props?.sections[0]?.fields;
		console.log('Checking if all fields are prefilled:', fields);
		if (fields) {
			const allPrefilled = fields.every((f: unknown) => {
				const field = f as { name: string };
				return prefilledAnswers.some(a => a.questionName === field.name);
			});
			if (allPrefilled) {
				nextPage();
			}
		}
	}, []);

	useEffect(() => {
		if (formRenderMode === FormRenderMode.design) {
			// Reset answers when switching to design mode
			setAnswers([]);
			setCurrentPage(0);
			setPrevPage([]);
		} else if (formRenderMode === FormRenderMode.preview) {
			setAnswers(initialAnswers ?? prefilledAnswers ?? []);
		}
	}, [formRenderMode]);

	const submitForm = async (a: IQuestionAnswer[]) => {
		const updatedAnswers = [...a];
		const sections = layout.layouts?.[0]?.props?.sections;
		const allBlocks = getAllBlocksFromLayout(layout);
		setIsValidating(true);
		const valid = await validateFormClientSide(
			formRequestId,
			ownerId,
			allBlocks,
			sections,
			updatedAnswers,
			tempData,
			goToNextPage,
			{ createTicketReservation, extendReservation, findReservationById, getTicketDetails },
			setErrors,
			true,
			layout,
			onWaitList
		);
		setIsValidating(false);

		if (!valid) {
			return;
		}

		const ensuredAnswers = await ensureAnswers(updatedAnswers, i18n.countryCode);
		const filtered = filterOutConditionals(layout, ensuredAnswers);
		const toSubmit = filtered.map(a => {
			const { questionName, answer, data, type } = a;
			return {
				questionName,
				data,
				type,
				...answer
			};
		});
		const res = await onSubmit?.(toSubmit);
		if (res.submitted || res.data) {
			await onSubmitted?.(res);
		} else {
			// Update errors to include goToPage functionality
			setErrors?.(prev => {
				const final = { ...prev };
				for (const [key, value] of Object.entries(final)) {
					const pageNo = sections.findIndex(s => s.fields.find(f => f.name === key));

					final[key] = {
						message: value?.message,
						...(pageNo >= 0 ? { setPage: () => goToNextPage(pageNo) } : {})
					};
				}
				return final;
			});
			const resUpdatedAnswers = updatedAnswers.map(a => {
				const matchingAnswerFromResponse = res.updatedAnswers?.find(resA => resA.questionName === a.questionName);
				if (matchingAnswerFromResponse) {
					const { questionName, data, type, ...rest } = matchingAnswerFromResponse;
					return {
						data: data ?? a.data,
						type,
						questionName,
						answer: rest
					};
				} else
					return {
						data: a.data,
						type: a.type,
						questionName: a.questionName,
						answer: a.answer
					};
			});
			setAnswers(resUpdatedAnswers);
		}
	};

	const overlayOn = surveyState === SurveyState.submitting || isValidating;

	const goToPreviousPage: IFormRuntimeContext['goToPreviousPage'] = () => {
		clearSubmissionState?.();
		const newPrevPage = [...(prevPage ?? [])];
		const last = newPrevPage.pop();
		setCurrentPage?.(last ?? 0);
		setPrevPage?.([...newPrevPage]);
	};

	const getQuestionStyle = (questionStyleType: QuestionStyleTypes, fallback: QuestionStyle, questionId?: string) => {
		const questionStyles: QuestionStyles = styles ?? DEFAULT_QUESTION_STYLES;

		const style: QuestionStyle = {
			color: questionStyles?.[questionStyleType]?.color ?? fallback.color,
			fontFamily: questionStyles?.[questionStyleType]?.fontFamily ?? fallback.fontFamily,
			fontSize: questionStyles?.[questionStyleType]?.fontSize ?? fallback.fontSize,
			fontStyle: questionStyles?.[questionStyleType]?.fontStyle ?? fallback.fontStyle,
			fontWeight: questionStyles?.[questionStyleType]?.fontWeight ?? fallback.fontWeight,
			textDecoration: questionStyles?.[questionStyleType]?.textDecoration ?? fallback.textDecoration,
			backgroundColour: questionStyles?.[questionStyleType]?.backgroundColour ?? fallback.backgroundColour,
			primaryColour: questionStyles?.[questionStyleType]?.primaryColour ?? fallback.primaryColour,
			minHeight: questionStyles?.[questionStyleType]?.minHeight
		};
		return style;
	};

	return (
		<FormRuntimeContext.Provider
			value={{
				formId,
				submitForm,
				submitButtonDisabled,
				currentPage,
				prevPage,
				goToNextPage: (currentPage ?? 0) < sections.length - 1 ? nextPage : undefined,
				goToPreviousPage: prevPage && prevPage.length > 0 ? goToPreviousPage : undefined,
				owner: { ownerId, ownerType },
				target: targetInfo,
				questionStyles: styles ?? DEFAULT_QUESTION_STYLES,
				getQuestionStyle,
				inLocalSurveyMode,
				getPointsTotal,
				surveyState,
				formRenderMode,
				complete,
				uploadFile,
				answerQuestion,
				getAnswers,
				getAnswer,
				sections,
				layout,
				submitButtonLabel,
				formRequestId
			}}
		>
			{children}
			<LoadingOverlay loading={overlayOn} />
		</FormRuntimeContext.Provider>
	);
};
